import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Title } from 'react-admin';
import { useHistory, useLocation } from 'react-router-dom';
import { DocumentReviewState } from '../../../interfaces/document';
import { OpsReferral, OpsReferralListResponse } from '../../../interfaces/referral';
import { getClinicsForSelect } from '../../../lib/clinic';
import { cohortLabel, PROGRAM_COHORT_OPTIONS } from '../../../lib/referrals/cohorts';
import referralService from '../../../services/referral';
import { CustomTable } from '../../common/CustomTable';
import { PageHeader } from '../../common/PageHeader/PageHeader';
import '../../common/tab-section.css';
import { PROPAGATION_TARGETS } from '../../document/Details/PropagationStatus';
import {
  getReviewStatePresentation,
  isNeedingAttentionSelection,
  REVIEW_STATE_PRESENTATION,
} from '../../document/shared';
import {
  forgetOpenedFromList,
  referralOpenPath,
  rememberOpenedFromList,
} from './referralHubPaths';
import {
  MAX_SEARCH_LENGTH,
  REFERRAL_HUB_NEEDING_ATTENTION,
  REFERRAL_HUB_REVIEW_STATES,
  ReferralHubFilters,
  useReferralHubFilters,
} from './useReferralHubFilters';

/** Hidden on screen, still read by a screen reader. */
const VISUALLY_HIDDEN = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
  m: '-1px',
  p: 0,
} as const;

/**
 * The State column's chip for a discarded referral, with its reason on hover and focus.
 *
 * The chip keeps its own name ("Discarded"); the reason is its description, read from a visually
 * hidden span by `aria-describedby`. The reason never lands in an attribute: Sentry records a
 * clicked element's `aria-label`, `title` and `name` in its breadcrumbs, and the reason is free
 * text an operator wrote about a patient. That is why the Tooltip's title is an element (a string
 * becomes the child's `aria-label`, or with `describeChild` its `title`). Focusable, and
 * described, only when there is a reason to show.
 */
const DiscardedChip: React.FC<{ referralId: number; reason: string | null }> = ({
  referralId,
  reason,
}) => {
  const chip = <Chip size='small' variant='outlined' label='Discarded' />;
  if (!reason) {
    return chip;
  }

  const reasonId = `referral-${referralId}-discard-reason`;
  // The reason sits beside the focusable span, not in it: inside, it would be read once as
  // content and again as the description.
  return (
    <>
      <Tooltip describeChild title={<span>{reason}</span>}>
        <span tabIndex={0} aria-describedby={reasonId}>
          {chip}
        </span>
      </Tooltip>
      <Box component='span' id={reasonId} sx={VISUALLY_HIDDEN}>
        {reason}
      </Box>
    </>
  );
};

/** The filters picked from a menu that stays open across selections. */
type MultiFilterKey = 'clinicIds' | 'reviewStates' | 'systemSources';

// Hand-rolled rather than `hooks/useDebounce`: that hook fixes the delay at 700 ms and cannot
// express the `searchInput === filters.search` guard below, which keeps a search restored from
// the URL from being written back as if it had been typed.
const SEARCH_DELAY_MS = 400;

// One formatter for the page, not a `toLocaleString()` per row per render.
const CREATED_FORMAT = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'short',
  timeStyle: 'short',
});

const COLUMNS = [
  { key: 'patient', label: 'Patient' },
  { key: 'clinic', label: 'Clinic' },
  { key: 'cohort', label: 'Cohort' },
  { key: 'dataQuality', label: 'Data quality' },
  { key: 'lastEdit', label: 'Last edit reached' },
  { key: 'state', label: 'State' },
  { key: 'created', label: 'Created' },
];

/**
 * Only what needs attention: a failed or pending delivery. A fully synced edit shows nothing.
 *
 * `propagation` is guarded although the type says it is always there: referrals edited before
 * the ledger existed have none (the card's `PropagationStatus` guards the same case), and one
 * such row must not take the whole list down. The targets are iterated in a fixed order, not
 * the response's key order, so the chips line up from row to row.
 */
const LastEditCell: React.FC<{ referral: OpsReferral }> = ({ referral }) => {
  const propagation = referral.propagation ?? {};
  const problems = PROPAGATION_TARGETS.flatMap(({ key, label }) => {
    const status = propagation[key];
    return status === 'failed' || status === 'pending' ? [{ key, label, status }] : [];
  });
  if (problems.length === 0) {
    return (
      <Typography variant='caption' color='text.secondary'>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
      {problems.map(({ key, label, status }) => (
        <Chip
          key={key}
          size='small'
          variant='outlined'
          color={status === 'failed' ? 'error' : 'warning'}
          label={`${label}: ${status}`}
        />
      ))}
    </Box>
  );
};

/**
 * The Referrals section's list.
 *
 * Referrals as the unit of work, not documents: one row per referral, filterable by the data
 * quality of the document it came from, so "what needs to be reviewed and solved" is one
 * filter away. Opening a row shows the referral next to its source document.
 */
const ReferralsHubList: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  // Remembered for the detail page, so its Back returns to the list as it was filtered.
  const listSearch = location.search;
  useEffect(() => {
    forgetOpenedFromList();
  }, []);
  const { filters, updateFilter, reset, showNeedingAttention } = useReferralHubFilters();
  const [result, setResult] = useState<OpsReferralListResponse | null>(null);
  // True from the first paint: the first load starts in an effect, and a `false` here showed
  // "No referrals match these filters." for a frame before it.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clinics, setClinics] = useState<Array<{ id: number; name: string }>>([]);
  const [searchInput, setSearchInput] = useState(filters.search);
  // A multi-select's selection while its menu is open. Committed on close, so choosing five
  // clinics is one request rather than five.
  const [draft, setDraft] = useState<Partial<Pick<ReferralHubFilters, MultiFilterKey>>>({});

  // The latest request wins. A slow response for an old filter must not overwrite a newer one.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await referralService.listOpsReferrals(filters);
      if (current === requestId.current) {
        setResult(response);
      }
    } catch {
      if (current === requestId.current) {
        // Said plainly, and the stale rows cleared: an empty-looking list after a failure
        // would read as "nothing to work on".
        setResult(null);
        setError('Could not load referrals. Try again, or reload the page.');
      }
    } finally {
      if (current === requestId.current) {
        setLoading(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getClinicsForSelect()
      .then((list) => setClinics(list.map(({ id, name }) => ({ id, name }))))
      .catch(() => setClinics([]));
  }, []);

  // Typing does not query per keystroke; the URL and the request follow once it settles.
  const committedSearch = useRef(filters.search);
  useEffect(() => {
    if (searchInput === filters.search) {
      return undefined;
    }
    const timer = setTimeout(() => {
      committedSearch.current = searchInput;
      updateFilter('search', searchInput);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search, updateFilter]);

  // A search that changed under the box — the URL was navigated to (the menu item, an in-app
  // link, Clear) rather than typed — replaces what the box shows.
  useEffect(() => {
    if (filters.search !== committedSearch.current) {
      committedSearch.current = filters.search;
      setSearchInput(filters.search);
    }
  }, [filters.search]);

  const clinicName = useMemo(() => {
    const byId = new Map(clinics.map(({ id, name }) => [id, name]));
    return (id: number | null) => (id === null ? '—' : (byId.get(id) ?? `Clinic ${id}`));
  }, [clinics]);

  const needingAttention = isNeedingAttentionSelection(
    filters.reviewStates,
    REFERRAL_HUB_NEEDING_ATTENTION,
  );

  const multiValue = <K extends MultiFilterKey>(key: K): ReferralHubFilters[K] =>
    (draft[key] as ReferralHubFilters[K] | undefined) ?? filters[key];
  const commitDraft = (key: MultiFilterKey) => {
    const value = draft[key];
    setDraft(({ [key]: _committed, ...rest }) => rest);
    if (value !== undefined && value.join(',') !== filters[key].join(',')) {
      updateFilter(key, value as never);
    }
  };

  const rows = useMemo(
    () =>
      (result?.referrals ?? []).map((referral) => {
        const review = getReviewStatePresentation(referral.reviewState);
        const open = () => {
          const path = referralOpenPath(referral);
          rememberOpenedFromList(path, listSearch);
          history.push(path);
        };

        return {
          id: referral.id,
          // A convenience for the mouse; the patient name below is the real, focusable control.
          onClick: open,
          patient: (
            <Box>
              <Link
                component='button'
                variant='body2'
                fontWeight={500}
                underline='hover'
                sx={{ textAlign: 'left' }}
                onClick={(event: React.MouseEvent) => {
                  // The row's own handler would push the same path a second time.
                  event.stopPropagation();
                  open();
                }}>
                {referral.patientName}
              </Link>
              <Typography variant='caption' color='text.secondary' display='block'>
                #{referral.id}
                {referral.crmId ? ` · ${referral.crmId}` : ''}
                {referral.clinicReferralId ? ` · ${referral.clinicReferralId}` : ''}
              </Typography>
              {referral.documentId === null && (
                // A legacy intake: its page opens, but has no document to show beside it.
                <Typography variant='caption' color='text.secondary' display='block'>
                  No source document
                </Typography>
              )}
            </Box>
          ),
          clinic: clinicName(referral.clinicId),
          cohort: cohortLabel(referral.systemSource),
          dataQuality: (
            <Tooltip title={review.description}>
              {/*
                Focusable so the explanation is reachable without a mouse: a Tooltip anchors to
                a DOM node, and a bare Chip is not focusable. Same fix as the Documents list.
              */}
              <span tabIndex={0}>
                <Chip
                  size='small'
                  label={review.label}
                  sx={{ color: '#fff', bgcolor: review.color, textTransform: 'capitalize' }}
                />
              </span>
            </Tooltip>
          ),
          lastEdit: <LastEditCell referral={referral} />,
          state: referral.discardedAt ? (
            <DiscardedChip referralId={referral.id} reason={referral.discardReason} />
          ) : referral.userId ? (
            <Chip size='small' variant='outlined' color='success' label='Member linked' />
          ) : (
            <Chip size='small' variant='outlined' label='Not yet a member' />
          ),
          created: CREATED_FORMAT.format(new Date(referral.createdAt)),
        };
      }),
    [result, clinicName, history, listSearch],
  );

  return (
    <Box>
      <Title title='Referrals' />
      <Box className='tab-section'>
        <PageHeader breadcrumbs={[{ label: 'Referrals', active: true }]} />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
          <TextField
            size='small'
            label='Search'
            placeholder='Patient name, referral id, LeadSquared id, clinic referral id'
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            inputProps={{ maxLength: MAX_SEARCH_LENGTH }}
            sx={{ minWidth: 280 }}
          />

          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel id='referrals-clinic'>Clinic</InputLabel>
            <Select
              labelId='referrals-clinic'
              multiple
              value={multiValue('clinicIds')}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  clinicIds: (event.target.value as number[]).map(Number),
                }))
              }
              onClose={() => commitDraft('clinicIds')}
              input={<OutlinedInput label='Clinic' />}
              renderValue={(ids) => (ids as number[]).map(clinicName).join(', ')}
            >
              {clinics.map((clinic) => (
                <MenuItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel id='referrals-quality'>Data quality</InputLabel>
            <Select
              labelId='referrals-quality'
              multiple
              value={multiValue('reviewStates')}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  reviewStates: event.target.value as DocumentReviewState[],
                }))
              }
              onClose={() => commitDraft('reviewStates')}
              input={<OutlinedInput label='Data quality' />}
              renderValue={(states) =>
                (states as DocumentReviewState[])
                  .map((state) => getReviewStatePresentation(state).label)
                  .join(', ')
              }
            >
              {REFERRAL_HUB_REVIEW_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {REVIEW_STATE_PRESENTATION[state].label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{ minWidth: 180 }}>
            <InputLabel id='referrals-cohort'>Cohort</InputLabel>
            <Select
              labelId='referrals-cohort'
              multiple
              value={multiValue('systemSources')}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  systemSources: event.target.value as string[],
                }))
              }
              onClose={() => commitDraft('systemSources')}
              input={<OutlinedInput label='Cohort' />}
              renderValue={(sources) => (sources as string[]).map(cohortLabel).join(', ')}
            >
              {PROGRAM_COHORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={filters.includeDiscarded}
                onChange={(event) => updateFilter('includeDiscarded', event.target.checked)}
              />
            }
            label='Include discarded'
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
          <Typography variant='caption' color='text.secondary'>
            Quick views:
          </Typography>
          <Button
            size='small'
            variant={needingAttention ? 'contained' : 'outlined'}
            aria-pressed={needingAttention}
            startIcon={<WarningAmberIcon />}
            // A toggle, as on Documents: pressing it again clears the view without also
            // wiping clinic, cohort and search the way "Clear filters" does.
            onClick={() =>
              showNeedingAttention(needingAttention ? [] : [...REFERRAL_HUB_NEEDING_ATTENTION])
            }
          >
            Needs attention
          </Button>
          <Button
            size='small'
            onClick={() => {
              setSearchInput('');
              reset();
            }}
          >
            Clear filters
          </Button>
        </Box>

        {error && (
          <Alert
            severity='error'
            sx={{ mb: 2 }}
            action={
              <Button color='inherit' size='small' onClick={load}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!error && (
          <CustomTable
            columns={COLUMNS}
            rows={rows}
            loading={loading}
            defaultText='No referrals match these filters.'
            pagination={{
              rowsPerPageOptions: [20, 50, 100],
              rowPerPage: filters.limit,
              page: filters.page,
              totalCount: result?.pagination.total ?? 0,
              onPageChange: (page) => updateFilter('page', page),
              onRowsPerPageChange: (event) => updateFilter('limit', Number(event.target.value)),
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ReferralsHubList;
